import type { FormattedSearchResult, SearchContext, SolrResponse } from './types'
import { SolrParamsKey } from '#shared/solr'

interface JournalSectionDocument {
  id: string
  title?: string
  bodytext: string
}

function normalizeHighlightHtml(text: string): string {
  return text
    .replace(/<\/?title[^>]*>/gi, '')
    .replace(/&lt;span class=("|')highlight\1&gt;/gi, '<span class="highlight">')
    .replace(/&lt;\/span&gt;/gi, '</span>')
    .trim()
}

export function useJournalArticleSearchStrategy() {
  const getContentTypeLabel = () => 'Tidsskriftartikkel'

  const getSearchPlaceholder = () => 'Søk i denne artikkelen'

  const buildQueryParams = (query: string, context: SearchContext) => ({
    [SolrParamsKey.SearchLiterature]: {
      searchTerm: query.trim(),
      id: `${context.contentId}*`,
      contentType: ['journal_section'],
    },
  })

  const getCollectionKey = (): 'journal' => 'journal'
  const getEndpoint = (): 'search' => 'search'

  const formatResults = (solrData: SolrResponse): FormattedSearchResult[] => {
    const data = solrData as {
      response?: { docs?: unknown[] }
      highlighting?: Record<string, { bodytext?: string[], title?: string[] }>
    }

    const docs = data?.response?.docs as JournalSectionDocument[] | undefined
    if (!Array.isArray(docs)) {
      return []
    }

    // Production Solr returns some journal_section docs twice; the repeated ids collide as v-for keys downstream.
    const seenIds = new Set<string>()
    const uniqueDocs = docs.filter(doc => {
      if (seenIds.has(doc.id)) {
        return false
      }
      seenIds.add(doc.id)
      return true
    })

    return uniqueDocs.map((doc: JournalSectionDocument) => {
      const highlights = [
        ...(data?.highlighting?.[doc.id]?.bodytext || []).map((text: string) => ({ field: 'bodytext', text: normalizeHighlightHtml(text) })),
        ...(data?.highlighting?.[doc.id]?.title || []).map((text: string) => ({ field: 'title', text: normalizeHighlightHtml(text) })),
      ].filter(h => h.text && h.text.trim().length > 0)

      // Extract highlight text for snippet
      const bodyHighlights = highlights.filter(h => h.field === 'bodytext')

      // Create snippet from highlights or fallback to bodytext
      let snippet = ''
      if (bodyHighlights.length > 0 && bodyHighlights[0]) {
        // Use the first highlight as snippet
        snippet = bodyHighlights[0].text
      }
      else if (doc.bodytext) {
        // Fallback to truncated bodytext
        snippet = doc.bodytext.length > 100 ? `${doc.bodytext.substring(0, 100)}...` : doc.bodytext
      }

      return {
        id: doc.id,
        title: doc.title ?? '',
        snippet,
        highlights: bodyHighlights.map(h => h.text),
      }
    })
  }

  return {
    buildQueryParams,
    getCollectionKey,
    getEndpoint,
    formatResults,
    getContentTypeLabel,
    getSearchPlaceholder,
  }
}
